import MaterialCostInput from './MaterialCostInput';
import RandomBoardSettings from './RandomBoardSettings';
import SettingsSection from './SettingsSection';
import UnitSelector from './UnitSelector';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { SelectInputProps } from '@mui/material/Select/SelectInput';
import { DEFAULT_SETTINGS } from 'Constants';
import { useRecoilState } from 'recoil';
import { Materials, Settings as SettingsType, Theme, Units } from 'Types';

import NumberInput from 'designer/NumberInput';
import { settingsAtom } from 'state/recoilState';
import { convertNumber, numberFormatter } from 'utils/utils';

function Settings() {
  const [settings, setSettings] = useRecoilState(settingsAtom);

  const onSettingChanged = (update: Partial<SettingsType>) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      ...update,
    }));
  };

  const onThemeChange: SelectInputProps<Theme>['onChange'] = (event) => {
    onSettingChanged({ theme: event.target.value as Theme });
  };

  const onUnitChange = (units: Units) => {
    onSettingChanged({
      units,
      kerf: +numberFormatter.format(
        convertNumber(settings.kerf, settings.units, units)
      ),
      planeBuffer: +numberFormatter.format(
        convertNumber(settings.planeBuffer, settings.units, units)
      ),
      pxPerInch: Math.round(
        convertNumber(settings.pxPerInch, settings.units, units)
      ),
      random: {
        ...settings.random,
        size: {
          width: convertNumber(
            settings.random.size.width,
            settings.units,
            units
          ),
          height: convertNumber(
            settings.random.size.height,
            settings.units,
            units
          ),
        },
      },
    });
  };

  const onKerfChange = (kerf: number) => {
    onSettingChanged({ kerf });
  };

  const onPlaneBufferChange = (planeBuffer: number) => {
    onSettingChanged({ planeBuffer });
  };

  const onWasteChange = (waste: number) => {
    onSettingChanged({ waste: waste / 100 });
  };

  const onPxPerInchChange = (pxPerInch: number) => {
    onSettingChanged({ pxPerInch });
  };

  const onReset = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ pb: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h4" color="inherit">
          Settings
        </Typography>

        <Button color="primary" onClick={onReset}>
          <UndoIcon style={{ paddingRight: 4 }} />
          Reset
        </Button>
      </Box>

      <SettingsSection title="Units">
        <UnitSelector
          isCompact={false}
          value={settings.units}
          onChange={onUnitChange}
        />
        <br />
        <Typography variant="caption" display="block">
          Applies only to new boards. You can convert an existing board's units
          in the designer.
        </Typography>
      </SettingsSection>

      <SettingsSection title="Display">
        <Grid container spacing={2}>
          <Grid item>
            <FormControl variant="standard">
              <InputLabel id="theme-select">Theme</InputLabel>
              <Select
                labelId="theme-label"
                value={settings.theme}
                onChange={onThemeChange}
              >
                <MenuItem value={Theme.System}>System</MenuItem>
                <MenuItem value={Theme.Light}>Light</MenuItem>
                <MenuItem value={Theme.Dark}>Dark</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Box sx={{ width: 100 }}>
              <NumberInput
                id="px-per-inch"
                label={'PX per ' + settings.units}
                min={1}
                step={1}
                value={settings.pxPerInch}
                onChange={onPxPerInchChange}
              />
            </Box>
          </Grid>
        </Grid>
      </SettingsSection>

      <SettingsSection title="Material Loss">
        <Grid container spacing={2}>
          <Grid item>
            <NumberInput
              id="kerf"
              label="Kerf"
              step={1 / 8}
              value={settings.kerf}
              onChange={onKerfChange}
              size={80}
              units={settings.units}
            />
          </Grid>
          <Grid item>
            <NumberInput
              id="plane-buffer"
              label="Plane Buffer"
              step={1 / 8}
              value={settings.planeBuffer}
              onChange={onPlaneBufferChange}
              size={80}
              units={settings.units}
            />
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NumberInput
                id="other-waste"
                label="Other Waste"
                step={1}
                value={settings.waste * 100}
                onChange={onWasteChange}
                size={80}
                units="%"
              />
            </Box>
          </Grid>
        </Grid>
      </SettingsSection>

      <SettingsSection title="Material Cost">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          {Materials.map((material) => (
            <MaterialCostInput key={material} material={material} />
          ))}
        </Box>

        <Typography variant="caption" display="block">
          Wood species images provided by{' '}
          <a href="wood-database.com">wood-database.com</a>
        </Typography>
      </SettingsSection>

      <RandomBoardSettings />
    </Container>
  );
}

export default Settings;
